// Compare only Work Description shielding/mention references for Minor Amendment validation.
export interface WorkDescriptionMentionChangeResult {
  hasChanges: boolean;
  originalMentions: string[];
  revisedMentions: string[];
}

/**
 * Checks whether the shielding mentions embedded in a Work Description changed.
 * Plain wording changes are intentionally ignored so Minor Amendments can allow loose text corrections.
 *
 * @param originalValue - Approved Work Description HTML.
 * @param revisedValue - Proposed Work Description HTML.
 * @returns Whether the extracted mention reference set changed.
 */
export function hasWorkDescriptionMentionChanges(originalValue: string, revisedValue: string): boolean {
  return analyzeWorkDescriptionMentionChanges(originalValue, revisedValue).hasChanges;
}

/**
 * Extracts and compares Work Description mention references.
 * The comparison is order-insensitive because the selected shielding set, not sentence wording, is restricted.
 *
 * @param originalValue - Approved Work Description HTML.
 * @param revisedValue - Proposed Work Description HTML.
 * @returns The mention references found on each side and whether they differ.
 */
export function analyzeWorkDescriptionMentionChanges(
  originalValue: string,
  revisedValue: string
): WorkDescriptionMentionChangeResult {
  const originalMentions = extractWorkDescriptionMentionReferences(originalValue);
  const revisedMentions = extractWorkDescriptionMentionReferences(revisedValue);

  return {
    hasChanges: originalMentions.join('|') !== revisedMentions.join('|'),
    originalMentions,
    revisedMentions
  };
}

/**
 * Reads Quill mention references from saved Work Description HTML.
 * Uses data-id when available and falls back to the mention text for legacy saved spans.
 *
 * @param value - Work Description HTML or plain text.
 * @returns A normalized, sorted list of unique mention references.
 */
export function extractWorkDescriptionMentionReferences(value: string): string[] {
  if (!value) {
    return [];
  }

  const references = new Set<string>();
  const mentionSpanPattern = /<span\b([^>]*)>([\s\S]*?)<\/span>/gi;
  let match: RegExpExecArray | null;

  while ((match = mentionSpanPattern.exec(value)) !== null) {
    const attributes = match[1] ?? '';
    if (!isMentionSpan(attributes)) {
      continue;
    }

    const mentionId = getAttributeValue(attributes, 'data-id');
    if (!mentionId) continue;

    const mentionValue = getAttributeValue(attributes, 'data-value');
    const mentionText = stripHtml(match[2] ?? '');
    const reference = normalizeMentionReference(mentionId || mentionValue || mentionText);

    if (reference) {
      references.add(reference);
    }
  }

  return [...references].sort();
}

/**
 * Detects Quill mention spans without depending on browser DOM APIs.
 *
 * @param attributes - Raw HTML attributes from a span tag.
 * @returns Whether the span appears to be a Quill mention.
 */
function isMentionSpan(attributes: string): boolean {
  const className = getAttributeValue(attributes, 'class');
  return /\bmention\b/i.test(className) || /\bdata-id\s*=/i.test(attributes);
}

/**
 * Gets an attribute value from an HTML tag attribute string.
 *
 * @param attributes - Raw HTML attributes from a tag.
 * @param name - Attribute name to read.
 * @returns The attribute value or an empty string.
 */
function getAttributeValue(attributes: string, name: string): string {
  const pattern = new RegExp(`${name}\\s*=\\s*["']([^"']*)["']`, 'i');
  return pattern.exec(attributes)?.[1] ?? '';
}

/**
 * Removes nested markup from a mention label fallback.
 *
 * @param value - HTML fragment to normalize.
 * @returns Plain text content.
 */
function stripHtml(value: string): string {
  return value.replace(/<[^>]+>/g, ' ');
}

/**
 * Normalizes references before comparing them.
 *
 * @param value - Raw mention ID, value, or label.
 * @returns A stable comparable reference.
 */
function normalizeMentionReference(value: string): string {
  return value.replace(/\s+/g, ' ').trim().toLowerCase();
}
